import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box,
    TextField,
    Snackbar,
    Alert,
    Typography,
    Grid,
    FormControlLabel,
    Checkbox,
    Button,
} from '@mui/material';
import Loader from '../../../components/Loader';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import Modal from '../../../components/general/modal';
import { getTaxRatesService, createTaxRateService, updateTaxRateService, deleteTaxRateService } from '../../../services/billing';
import { TaxRate } from './types';
import { useSnackBarState } from '../../../hooks';
import { Translate } from 'react-localize-redux';
import { ButtonAdd } from '../../../components/general/mini_components';

interface TaxRatesConfigurationProps {
    uuidInvestigation: string;
}

const TaxRatesConfiguration: React.FC<TaxRatesConfigurationProps> = ({ uuidInvestigation }) => {
    const [loading, setLoading] = useState(true);
    const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
    const [editingRate, setEditingRate] = useState<TaxRate | null>(null);
    const [deletingRate, setDeletingRate] = useState<TaxRate | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();

    // Form state
    const [formName, setFormName] = useState('');
    const [formPercentage, setFormPercentage] = useState('');
    const [formIsDefault, setFormIsDefault] = useState(false);
    const [formActive, setFormActive] = useState(true);

    const fetchTaxRates = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getTaxRatesService(uuidInvestigation);
            if (response.status === 200) {
                setTaxRates(response.taxRates);
            }
        } catch (error) {
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.error.load', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [uuidInvestigation]);

    useEffect(() => {
        if (uuidInvestigation) {
            fetchTaxRates();
        }
    }, [uuidInvestigation, fetchTaxRates]);

    const headCells = [
        { id: 'name', alignment: 'left', label: <Translate id="hospital.billing.tax_rates.name" /> },
        { id: 'percentage', alignment: 'left', label: <Translate id="hospital.billing.tax_rates.percentage" /> },
        { id: 'isDefault', alignment: 'left', label: <Translate id="hospital.billing.tax_rates.default" /> },
        { id: 'active', alignment: 'left', label: <Translate id="hospital.billing.tax_rates.active" /> },
    ];

    const rows = useMemo(() => {
        return taxRates.map(rate => ({
            id: rate.id!,
            name: rate.name,
            percentage: rate.percentage + '%',
            isDefault: rate.isDefault ? '✓' : '',
            active: rate.active ? '✓' : '✗',
        }));
    }, [taxRates]);

    const resetForm = () => {
        setFormName('');
        setFormPercentage('');
        setFormIsDefault(false);
        setFormActive(true);
    };

    const resetModal = () => {
        setShowModal(false);
        setShowCreateModal(false);
        setEditingRate(null);
        setDeletingRate(null);
        resetForm();
    };

    const handleEdit = (id: number) => {
        const rate = taxRates.find(r => r.id === id);
        if (rate) {
            setEditingRate(rate);
            setFormName(rate.name);
            setFormPercentage(rate.percentage.toString());
            setFormIsDefault(rate.isDefault || false);
            setFormActive(rate.active !== false);
            setShowModal(true);
        }
    };

    const handleDeleteConfirm = (id: number) => {
        const rate = taxRates.find(r => r.id === id);
        if (rate) {
            setDeletingRate(rate);
            setShowModal(true);
        }
    };

    const handleCreate = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const handleSaveCreate = async () => {
        if (!formName.trim() || !formPercentage.trim()) return;
        const percentage = parseFloat(formPercentage);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.error.invalid_percentage', severity: 'error' });
            return;
        }
        try {
            setSaving(true);
            const response = await createTaxRateService(uuidInvestigation, {
                name: formName.trim(),
                percentage,
                isDefault: formIsDefault,
                active: formActive,
            });
            if (response.status === 200 || response.status === 201) {
                setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.success.created', severity: 'success' });
                resetModal();
                await fetchTaxRates();
            }
        } catch (error: any) {
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.error.create', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingRate || !formName.trim() || !formPercentage.trim()) return;
        const percentage = parseFloat(formPercentage);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.error.invalid_percentage', severity: 'error' });
            return;
        }
        try {
            setSaving(true);
            const response = await updateTaxRateService(uuidInvestigation, editingRate.uuid!, {
                name: formName.trim(),
                percentage,
                isDefault: formIsDefault,
                active: formActive,
            });
            if (response.status === 200) {
                setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.success.updated', severity: 'success' });
                resetModal();
                await fetchTaxRates();
            }
        } catch (error: any) {
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.error.update', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingRate) return;
        try {
            setSaving(true);
            await deleteTaxRateService(uuidInvestigation, deletingRate.uuid!);
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.success.deleted', severity: 'success' });
            resetModal();
            await fetchTaxRates();
        } catch (error: any) {
            setShowSnackbar({ show: true, message: 'hospital.billing.tax_rates.error.delete', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const actions = [
        { type: 'edit', func: (id: number) => handleEdit(id) },
        { type: 'delete', func: (id: number) => handleDeleteConfirm(id) },
    ];

    const renderFormFields = () => (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    autoFocus
                    fullWidth
                    label={<Translate id="hospital.billing.tax_rates.name" />}
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    variant="outlined"
                    disabled={saving}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label={<Translate id="hospital.billing.tax_rates.percentage" />}
                    value={formPercentage}
                    onChange={(e) => setFormPercentage(e.target.value)}
                    variant="outlined"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    disabled={saving}
                />
            </Grid>
            <Grid item xs={6}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formIsDefault}
                            onChange={(e) => setFormIsDefault(e.target.checked)}
                            disabled={saving}
                        />
                    }
                    label={<Translate id="hospital.billing.tax_rates.default" />}
                />
            </Grid>
            <Grid item xs={6}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formActive}
                            onChange={(e) => setFormActive(e.target.checked)}
                            disabled={saving}
                        />
                    }
                    label={<Translate id="hospital.billing.tax_rates.active" />}
                />
            </Grid>
        </Grid>
    );

    const renderModalContent = () => {
        if (editingRate) {
            return renderFormFields();
        }
        if (deletingRate) {
            return (
                <Typography variant="body1">
                    <Translate id="hospital.billing.tax_rates.confirm_delete" /> <strong>{deletingRate.name} ({deletingRate.percentage}%)</strong>?
                </Typography>
            );
        }
        return null;
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <ButtonAdd onClick={handleCreate} />
            </Box>

            <EnhancedTable
                noHeader
                noSelectable
                headCells={headCells}
                rows={rows}
                actions={actions}
            />

            {/* Edit / Delete modal */}
            <Modal
                open={showModal}
                title={editingRate
                    ? <Translate id="hospital.billing.tax_rates.edit_title" />
                    : <Translate id="hospital.billing.tax_rates.delete_title" />
                }
                closeModal={resetModal}
                confirmAction={editingRate ? handleSaveEdit : handleDelete}
            >
                {renderModalContent()}
            </Modal>

            {/* Create modal */}
            <Modal
                open={showCreateModal}
                title={<Translate id="hospital.billing.tax_rates.create_title" />}
                closeModal={resetModal}
                confirmAction={handleSaveCreate}
            >
                {renderFormFields()}
            </Modal>

            <Snackbar
                open={showSnackbar.show}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={showSnackbar.severity || 'info'} sx={{ width: '100%' }}>
                    {showSnackbar.message && (
                        typeof showSnackbar.message === 'string' && showSnackbar.message.startsWith('hospital.')
                            ? <Translate id={showSnackbar.message} />
                            : showSnackbar.message
                    )}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TaxRatesConfiguration;
